
import * as React from "react";
import { db } from '../../../firebase-info';
import {
  collection,
  doc,
  query,
  onSnapshot,
  where,
} from "firebase/firestore";
import {AuthContext, NeighbourContext } from "../contexts/contexts.js";
import { getDataOfRefs } from "../../utils/firestore-utils.jsx";

const NeighbourContextComponent = ({ children }) => {
    const authContextValue = React.useContext(AuthContext);
    const [neighbourData, setNeighbourData] = React.useState(null);
    const [communityData, setCommunityData] = React.useState(null);
    const [propertiesData, setPropertiesData] = React.useState([]);
    const [facilitiesData, setFacilitiesData] = React.useState([]);
    const [neighboursData, setNeighboursData] = React.useState([]);
    const [deliveriesData, setDeliveriesData] = React.useState([]);
    
    const [loadingProperties, setLoadingProperties] = React.useState(false)
    const [loadingFacilities, setLoadingFacilities] = React.useState(false)
    const [loadingNeighbours, setLoadingNeighbours] = React.useState(false)
    const [loadingDeliveries, setLoadingDeliveries] = React.useState(false)
    const [loadingCommunity, setLoadingCommunity] = React.useState(true)
    const [loading, setLoading] = React.useState(true)

    const subscribeToNeighbourInfo = async (id) => {
      return onSnapshot(doc(db, "neighbours", id), async (neighbour) => {
        const neighbourData = neighbour.data();

        if(neighbourData){
          const neighbourPropertiesData = await getDataOfRefs(neighbourData.properties);
          neighbourData.propertiesData = neighbourPropertiesData;
  
          setNeighbourData({...neighbourData, ref: neighbour.ref});
  
          subscribeToCommunityInfo(neighbourData?.communityRef.id);
        }
      })
    }
    const subscribeToCommunityInfo = async (id) => {
      return onSnapshot(doc(db, "communities", id), async (community) => {
        const commData = community.data();
        setCommunityData({...commData, ref: community.ref})
        setLoadingCommunity(false)
      })
    }

    const loadActiveNeighbourInfo = async () => {
      if (!authContextValue.loading) {
        setLoadingCommunity(true);
        const userData = authContextValue.userData;
        if(userData?.activeNeighbour) {
            subscribeToNeighbourInfo(userData.activeNeighbour.id);
            setPropertiesData([])
            setFacilitiesData([])
            setNeighboursData([])
        }
      }
    }

    const loadProperties = () => {
      if (!authContextValue.loading && !loadingCommunity && communityData){
        subscribeToPropertiesInfo(communityData?.ref);
      }
    }
    const subscribeToPropertiesInfo = async (commRef) => {
      setLoadingProperties(true);
      const q = query(collection(db, "properties"), where("communityRef", "==", commRef));
      onSnapshot(q, async (querySnapshot) => {
        const prData = [];
        querySnapshot.forEach((p) => {
          prData.push({...p.data(), ref: p.ref});
        });
        setPropertiesData(prData)
        setLoadingProperties(false)
      })
    }

    const loadFacilities = () => {
      if (!authContextValue.loading && !loadingCommunity && communityData){
        subscribeToFacilitiesInfo(communityData?.ref);
      }
    }
    const subscribeToFacilitiesInfo = async (commRef) => {
      setLoadingFacilities(true)
      const q = query(collection(db, "facilities"), where("communityRef", "==", commRef));
      onSnapshot(q, async (querySnapshot) => {
        const facData = [];
        querySnapshot.forEach((f) => {
          facData.push({...f.data(), ref: f.ref});
        });
        setFacilitiesData(facData)
        setLoadingFacilities(false);
      })
    }

    const loadNeighbours = () => {
      if (!authContextValue.loading && !loadingCommunity && communityData){
        subscribeToNeighboursInfo(communityData?.ref);
      }
    }
    const subscribeToNeighboursInfo = async (commRef) => {
      setLoadingNeighbours(true)
      const q = query(collection(db, "neighbours"), where("communityRef", "==", commRef));
      onSnapshot(q, async (querySnapshot) => {
        const neighData = [];
        querySnapshot.forEach((n) => {
          neighData.push({...n.data(), ref: n.ref});
        });
        setNeighboursData(neighData);
        setLoadingNeighbours(false);
      })
    }

    const loadDeliveries = () => {
      if (!authContextValue.loading && neighbourData){
        subscribeToDeliveriesInfo(neighbourData?.ref);
      }
    }
    const subscribeToDeliveriesInfo = async (neighbourRef) => {
      setLoadingDeliveries(true)
      const q = query(collection(db, "deliveries"), where("toNeighbourRef", "==", neighbourRef));
      onSnapshot(q, async (querySnapshot) => {
        const delivData = [];
        querySnapshot.forEach((d) => {
          delivData.push({...d.data(), ref: d.ref});
        });
        setDeliveriesData(delivData);
        setLoadingDeliveries(false);
      })
    }

    React.useEffect(() => {
      setLoading(loadingCommunity || loadingProperties || loadingFacilities || loadingNeighbours || loadingDeliveries)
    }, [loadingCommunity, loadingProperties, loadingFacilities, loadingNeighbours, loadingDeliveries])
    
    React.useEffect(() => {
      loadActiveNeighbourInfo()
    }, []);


    React.useEffect(() => {
      if(!authContextValue.loading){
        if(authContextValue.userData){
          loadActiveNeighbourInfo()
        }
        else{
          clearContext()
          setLoadingCommunity(false)
        }
      }
	}, [authContextValue.loading, authContextValue.userData]);

  const clearContext = () => {
    setNeighbourData(null);
    setCommunityData(null);
    setPropertiesData([]);
    setFacilitiesData([]);
    setNeighboursData([]);
    setDeliveriesData([]);
    setLoadingProperties(false)
    setLoadingFacilities(false)
    setLoadingNeighbours(false)
    setLoadingDeliveries(false)
    setLoadingCommunity(true)
  }

  const neighbourContextValue = React.useMemo(() =>
        ( {neighbourData, communityData, propertiesData, facilitiesData, neighboursData, deliveriesData, loadProperties, loadFacilities, loadNeighbours, loadDeliveries, loading } ), 
        [neighbourData, communityData, propertiesData, facilitiesData, neighboursData, deliveriesData, loadProperties, loadFacilities, loadNeighbours, loadDeliveries, loading]
    );

  return (
    <NeighbourContext.Provider value={neighbourContextValue}>
        {children}
    </NeighbourContext.Provider>
  )
}

export default NeighbourContextComponent;